import {
  AbTestEventEventData,
  RecoClickEvent,
  RecoViewEvent,
  WishlistCollectionData,
  WishlistItemData,
  WithCollectionUsageEvent,
} from '@cube/tracking-library-api';
import { CuratedCollectionItemData, ENTSWindow, WishlistEvent } from './ENTSWindow.js';
import {
  Article,
  CollectionEventContext,
  CollectionTrackingContext,
  Product,
  TrackingData,
  WishlistCollection,
  WishlistDataContainer,
} from '../../@types/wishlist.js';
import { config } from './config.js';
import { wishlistHeaderCountService } from './wishlist-header-count-service.js';
import { ProductsState } from '../context/wishlist-state.js';
import { ProductEventAggregate } from '../product-events/product-event-aggregate.js';

declare const window: ENTSWindow;

const getRawItemCount = () => wishlistHeaderCountService.getCount();

const getCurrentDateAsString = () => {
  const date = new Date();
  date.setMilliseconds(0);
  return date.toISOString();
};

const track = (data: WishlistEvent) => {
  window.CubeDataLayer ??= [];
  window.CubeDataLayer.push(data);
};

export const trackViewWishlist = (eventContext: string, products: Product[]) => {
  track({
    responsible_team_tracking: 'ENTS',
    event_name: 'view_wishlist',
    event_data: {
      is_interaction_event: false,
      event_category: 'wishlist',
      event_type: 'view',
      event_context: eventContext,
    },
    items: products.map((product, index) => {
      return createWishlistItemDataForProduct(product, index + 1);
    }),
  });
};

export const trackViewCollections = (
  eventContext: string,
  collections: WishlistCollection[],
  productsState: ProductsState,
) => {
  track({
    responsible_team_tracking: 'ENTS',
    event_name: 'view_collections',
    event_data: {
      is_interaction_event: false,
      event_category: 'wishlist',
      event_type: 'view',
      event_context: eventContext,
    },
    items: collections.map((collection, index) => {
      const productsAmount = productsState.getProductsToRender(collection.id).length;
      return createCollectionItem(collection, productsAmount, index + 1);
    }),
  });
};

export interface TrackedCuratedCollection {
  collection: WishlistCollection;
  quantity: number;
}

export const trackViewCuratedCollections = (eventContext: string, collections: TrackedCuratedCollection[]) => {
  track({
    responsible_team_tracking: 'ENTS',
    event_name: 'view_curated_collections',
    event_data: {
      is_interaction_event: false,
      event_category: 'wishlist',
      event_type: 'view',
      event_context: eventContext,
    },
    items: collections.map((entry, index) => createCuratedCollectionItem(entry.collection, entry.quantity, index + 1)),
  });
};

export const trackPageView = (type: string, wishlistEmpty: boolean, loggedIn: boolean) => {
  track({
    responsible_team_tracking: 'ENTS',
    event_name: 'page_view',
    event_data: {
      page_type: 'wishlist',
      page_content: [type, wishlistEmpty ? 'empty' : 'filled'],
      is_interaction_event: true,
      logged_in: loggedIn,
    },
  });
};

export const trackAddToWishlist = (data: WishlistDataContainer) => {
  track({
    event_name: 'add_to_wishlist',
    responsible_team_tracking: 'ENTS',
    responsible_team_feature: 'ENTS',
    currency: {
      code: config.currency ?? '',
    },
    event_data: {
      list_items_count: getRawItemCount(),
      is_interaction_event: true,
      event_category: data.tracking?.placement ?? 'not_provided',
      event_context: data.tracking?.context ?? 'not_provided',
    },
    promotion_data: data.tracking?.promotion,
    items: [
      {
        farb_id: data.colorId,
        artikel_id: data.articleId,
        syan: `${data.syan}`,
        add_date: getCurrentDateAsString(),
        position: data.tracking?.position,
      },
    ],
  });
};

export const productListView = (
  product: Product,
  article: Article | null,
  position: number,
  loggedIn: boolean,
  filteredListItemsCount?: number,
) => {
  track({
    event_name: 'list_view',
    responsible_team_tracking: 'ENTS',
    responsible_team_feature: 'ENTS',
    currency: {
      code: config.currency ?? '',
    },
    ecom_data: {
      list_name: 'wishlist',
    },
    event_data: {
      is_interaction_event: false,
      list_items_count: getRawItemCount(),
      filtered_list_items_count: filteredListItemsCount,
      logged_in: loggedIn,
    },
    items: [createWishlistItemDataForProductAndArticle(product, article, position)],
  });
};

export const trackSizeLayerOpen = (loggedIn: boolean, context: string = 'wishlist_size_menu') => {
  track({
    event_name: 'size_layer_open',
    responsible_team_tracking: 'ENTS',
    responsible_team_feature: 'ENTS',
    event_data: {
      event_category: 'size_layer',
      is_interaction_event: true,
      event_type: 'open',
      event_context: context,
      logged_in: loggedIn,
    },
  });
};

export const trackRemoveFromWishlist = (
  syan: number,
  colorId?: string,
  artikelId?: string,
  trackingdata?: TrackingData,
  isProductOnCollection: boolean = false,
) => {
  track({
    event_name: 'remove_from_wishlist',
    responsible_team_tracking: 'ENTS',
    responsible_team_feature: 'ENTS',
    currency: {
      code: config.currency ?? '',
    },
    event_data: {
      list_items_count: getRawItemCount(),
      is_interaction_event: true,
      event_category: trackingdata?.placement,
      event_context: trackingdata?.context,
      event_content: isProductOnCollection ? 'wishlist_and_collection' : 'wishlist',
    },
    promotion_data: trackingdata?.promotion,
    items: [
      {
        farb_id: colorId,
        syan: syan.toString(),
        artikel_id: artikelId,
        is_sponsored: false,
        add_date: trackingdata?.added,
        position: trackingdata?.position,
      },
    ],
  });
};

export const trackSizeSelected = (syan: number, article: Article, colorId?: string) => {
  track({
    event_name: 'size_selected',
    responsible_team_tracking: 'ENTS',
    responsible_team_feature: 'ENTS',
    event_data: {
      event_category: 'size_layer',
      is_interaction_event: true,
      event_type: 'click',
      event_context: 'wishlist_size_menu',
    },
    items: [
      {
        ...(colorId ? { farb_id: colorId } : {}),
        artikel_id: article.id,
        vertriebsinfo_id: article.salesInformationId,
        syan: syan.toString(),
        is_sponsored: false,
        is_available: !article.isOutOfStock,
        add_date: article.added,
      },
    ],
  });
};

export const trackProductActionMenuOpen = (
  availableActions: string[],
  syan: number,
  article: Article | null,
  colorId?: string,
) => {
  track({
    event_name: 'action_menu_open',
    responsible_team_tracking: 'ENTS',
    responsible_team_feature: 'ENTS',
    currency: {
      code: config.currency ?? '',
    },
    event_data: {
      is_interaction_event: true,
      event_content: availableActions,
    },
    items: [
      {
        ...(colorId ? { farb_id: colorId } : {}),
        ...(article ? { artikel_id: article.id } : {}),
        ...(article ? { vertriebsinfo_id: article.salesInformationId } : {}),
        syan: `${syan}`,
        ...(article ? { is_available: !article.isOutOfStock } : {}),
        ...(article ? { add_date: article.added } : {}),
      },
    ],
  });
};

export const trackCollectionActionMenuOpen = (
  availableActions: string[],
  collection: WishlistCollection,
  position: number,
  productsAmount: number,
) => {
  track({
    event_name: 'action_menu_open',
    responsible_team_tracking: 'ENTS',
    responsible_team_feature: 'ENTS',
    currency: {
      code: config.currency ?? '',
    },
    event_data: {
      is_interaction_event: true,
      event_content: availableActions,
    },
    items: [createCollectionItem(collection, productsAmount, position)],
  });
};

export const trackDeleteCollection = (
  collection: WishlistCollection,
  collectionTrackingContext: CollectionTrackingContext,
  removeItemFromFavorites: boolean,
) => {
  track({
    responsible_team_tracking: 'ENTS',
    event_name: 'delete_collection',
    event_data: createCollectionOperationEventData('delete', collectionTrackingContext.eventContext, [
      collection.name,
      ...(removeItemFromFavorites ? ['collection_and_wishlist'] : ['collection']),
    ]),
    items: [createCollectionItem(collection, collectionTrackingContext.productsAmount)],
  });
};

export const trackRemoveItemFromCollection = (
  product: Product,
  collection: WishlistCollection,
  eventContext: CollectionEventContext,
  removeItemFromFavorites: boolean,
) => {
  track({
    responsible_team_tracking: 'ENTS',
    event_name: 'remove_from_collection',
    event_data: createCollectionOperationEventData('remove', eventContext, [
      collection.name,
      ...(removeItemFromFavorites ? ['collection_and_wishlist'] : ['collection']),
    ]),
    items: [createWishlistItemDataForProduct(product)],
  });
};

export const trackAddItemToCollection = (
  product: Product,
  collection: WishlistCollection,
  eventContext: CollectionEventContext,
) => {
  track({
    responsible_team_tracking: 'ENTS',
    event_name: 'add_to_collection',
    event_data: createCollectionOperationEventData('add', eventContext, [collection.name]),
    items: [createWishlistItemDataForProduct(product)],
  });
};

export const trackRenameCollection = (
  collection: WishlistCollection,
  collectionTrackingContext: CollectionTrackingContext,
) => {
  track({
    responsible_team_tracking: 'ENTS',
    event_name: 'rename_collection',
    event_data: createCollectionOperationEventData('rename', collectionTrackingContext.eventContext, [collection.name]),
    items: [createCollectionItem(collection, collectionTrackingContext.productsAmount)],
  });
};

export const trackShareCollection = (
  collection: WishlistCollection,
  collectionTrackingContext: CollectionTrackingContext,
) => {
  track({
    responsible_team_tracking: 'ENTS',
    event_name: 'share_link',
    event_data: createCollectionOperationEventData('share', collectionTrackingContext.eventContext, [collection.name]),
    items: [createCollectionItem(collection, collectionTrackingContext.productsAmount)],
  });
};

export const trackCreateCollection = (collection: WishlistCollection, eventContext: CollectionEventContext) => {
  track({
    responsible_team_tracking: 'ENTS',
    event_name: 'create_collection',
    event_data: createCollectionOperationEventData('create', eventContext, [collection.name]),
    items: [createCollectionItem(collection, 0)],
  });
};

export const trackIsSharingAllowedToggled = (
  collection: WishlistCollection,
  collectionTrackingContext: CollectionTrackingContext,
) => {
  track({
    responsible_team_tracking: 'ENTS',
    event_name: 'toggle_switch',
    event_data: {
      is_interaction_event: true,
      event_type: 'switch',
      event_category: 'wishlist',
      event_context: 'share_link',
      event_content: [collection.isSharingAllowed ? 'on' : 'off'],
    },
    items: [createCollectionItem(collection, collectionTrackingContext.productsAmount)],
  });
};

const createCollectionOperationEventData = <T extends string>(
  operation: T,
  eventContext: CollectionEventContext,
  event_content: string[],
): WithCollectionUsageEvent<T> => {
  return {
    is_interaction_event: true,
    event_type: operation,
    event_category: 'wishlist',
    event_context: eventContext,
    event_content: event_content,
  };
};

const createWishlistItemDataForProduct = (product: Product, position?: number): WishlistItemData => {
  let selectedArticle: Article | null = null;
  if (!product.hasSizeOrQuantitySelection) {
    selectedArticle = product.articles[0];
  } else {
    const filteredArticles = product.articles.filter((value) => value.id === product.articleId);
    if (filteredArticles.length > 0) {
      selectedArticle = filteredArticles[0];
    }
  }
  return createWishlistItemDataForProductAndArticle(product, selectedArticle, position);
};

const createWishlistItemDataForProductAndArticle = (
  product: Product,
  selectedArticle: Article | null,
  position?: number,
): WishlistItemData => {
  return {
    ...(product.colorId ? { farb_id: product.colorId } : {}),
    ...(selectedArticle ? { artikel_id: selectedArticle.id } : {}),
    ...(selectedArticle ? { vertriebsinfo_id: selectedArticle.salesInformationId } : {}),
    syan: product.syan.toString(),
    is_sponsored: false,
    is_available: !product.isOutOfStock,
    add_date: product.added,
    ...(position ? { position: position } : {}),
  };
};

const createCollectionItem = (
  collection: WishlistCollection,
  productsAmount: number,
  position?: number,
): WishlistCollectionData => {
  return {
    item_type: 'collection',
    item_name: collection.name,
    is_shared: collection.isSharingAllowed,
    add_date: collection.created,
    quantity: productsAmount,
    ...(position ? { position: position } : {}),
  };
};

const createCuratedCollectionItem = (
  collection: WishlistCollection,
  productsAmount: number,
  position?: number,
): CuratedCollectionItemData => {
  return {
    item_type: 'curated_collection',
    item_name: collection.name,
    is_shared: true,
    add_date: collection.created,
    quantity: productsAmount,
    ...(position ? { position: position } : {}),
  };
};

export const trackClickAndReserveEvent = () => {
  track({
    event_name: 'hva_open',
    responsible_team_tracking: 'ENTS',
    responsible_team_feature: 'ENTS',
    event_data: {
      event_category: 'click_and_reserve',
      is_interaction_event: true,
      event_type: 'click',
      event_context: 'wishlist_action_menu',
    },
  });
};

export const trackFilterUsage = (isSort: boolean, filterOptions: string[]) => {
  const eventName = isSort ? 'sort_usage' : 'filter_usage';
  track({
    event_name: eventName,
    responsible_team_tracking: 'ENTS',
    responsible_team_feature: 'ENTS',
    event_data: {
      event_category: 'list_settings',
      event_type: 'click',
      is_interaction_event: true,
      event_content: filterOptions,
    },
  });
};

export const trackABTest = (abTestData: AbTestEventEventData) => {
  track({
    event_name: 'ab_test',
    responsible_team_tracking: 'ENTS',
    event_data: {
      ...abTestData,
      event_duration: 'temporary',
    },
  });
};

export const trackRecoView = (details: Partial<RecoViewEvent>) => {
  track({
    event_name: 'reco_view',
    responsible_team_tracking: 'ENTS',
    responsible_team_feature: 'ENTS',
    ...details,
  } as RecoViewEvent);
};

export const trackRecoClick = (details: Partial<RecoClickEvent>) => {
  track({
    event_name: 'reco_click',
    responsible_team_tracking: 'ENTS',
    responsible_team_feature: 'ENTS',
    ...details,
  } as RecoClickEvent);
};

interface PbInfoEventData {
  pbImpression?: string;
  pbClick?: string;
}

export const trackPbInfo = (eventContext: string, data: PbInfoEventData) => {
  const eventData: {
    event_context: string;
    pb_impression?: string;
    pb_click?: string;
  } = {
    event_context: eventContext,
  };
  if (data.pbImpression !== undefined) {
    eventData.pb_impression = data.pbImpression;
  }
  if (data.pbClick !== undefined) {
    eventData.pb_click = data.pbClick;
  }

  track({
    event_name: 'pb_info',
    responsible_team_tracking: 'ENTS',
    responsible_team_feature: 'ENTS',
    event_data: eventData,
  });
};

export const trackSnackbarUndoWishlistItemDelete = (product: Product) => {
  trackSnackbarUndoItemDelete('undo_delete_wishlist_item', product);
};

export const trackSnackbarUndoCollectionItemDelete = (product: Product) => {
  trackSnackbarUndoItemDelete('undo_delete_collection_item', product);
};

const trackSnackbarUndoItemDelete = (
  eventAction: 'undo_delete_wishlist_item' | 'undo_delete_collection_item',
  product: Product,
) => {
  track({
    event_name: 'snackbar_click',
    responsible_team_tracking: 'ENTS',
    event_data: {
      is_interaction_event: true,
      event_category: 'wishlist',
      event_type: 'click',
      event_context: 'snackbar',
      event_content: [eventAction],
    },
    items: [
      {
        artikel_id: product.articleId,
        farb_id: product.colorId,
        syan: `${product.syan}`,
        is_sponsored: false,
        is_available: !product.isOutOfStock,
        add_date: product.added,
      },
    ],
  });
};

export const trackUpdatesListView = (
  productEventAggregate: ProductEventAggregate,
  position: number,
  count: number,
  loggedIn: boolean,
  alreadySeen: boolean,
) => {
  track({
    event_name: 'list_view',
    responsible_team_tracking: 'ENTS',
    responsible_team_feature: 'ENTS',
    ecom_data: {
      list_name: 'wishlist_update_layer',
    },
    items: [
      {
        item_type: 'Product',
        syan: productEventAggregate.syan.toString(),
        ...(productEventAggregate.colorId ? { farb_id: productEventAggregate.colorId } : {}),
        ...(productEventAggregate.articleId ? { artikel_id: productEventAggregate.articleId } : {}),
        ...(productEventAggregate.salesInfoId ? { vertriebsinfo_id: productEventAggregate.salesInfoId } : {}),
        ...(position ? { position: position } : {}),
        campaign: productEventAggregate.campaigns,
      },
    ],
    event_data: {
      is_interaction_event: false,
      event_context: alreadySeen ? 'already_seen' : 'most_recent',
      list_items_count: count,
      logged_in: loggedIn,
    },
  });
};

export const trackUpdatesClick = (
  productEventAggregate: ProductEventAggregate,
  position: number,
  count: number,
  loggedIn: boolean,
) =>
  track({
    event_name: 'list_click',
    responsible_team_tracking: 'ENTS',
    responsible_team_feature: 'ENTS',
    ecom_data: {
      list_name: 'wishlist_update_layer',
    },
    items: [
      {
        item_type: 'Product',
        syan: productEventAggregate.syan.toString(),
        ...(productEventAggregate.colorId ? { farb_id: productEventAggregate.colorId } : {}),
        ...(productEventAggregate.articleId ? { artikel_id: productEventAggregate.articleId } : {}),
        ...(productEventAggregate.salesInfoId ? { vertriebsinfo_id: productEventAggregate.salesInfoId } : {}),
        position: position,
        campaign: productEventAggregate.campaigns,
        target_url: productEventAggregate.pdsURL,
      },
    ],
    event_data: {
      is_interaction_event: true,
      list_items_count: count,
      logged_in: loggedIn,
    },
  });

export const trackWishlistClick = (
  product: Product,
  selectedArticle: Article | null,
  position: number,
  count: number,
  loggedIn: boolean,
  targetUrl?: string,
) =>
  track({
    event_name: 'list_click',
    responsible_team_tracking: 'ENTS',
    responsible_team_feature: 'ENTS',
    ecom_data: {
      list_name: 'wishlist',
    },
    items: [
      {
        item_type: 'Product',
        syan: product.syan.toString(),
        ...(product.colorId ? { farb_id: product.colorId } : {}),
        ...(selectedArticle ? { artikel_id: selectedArticle.id } : {}),
        ...(selectedArticle ? { vertriebsinfo_id: selectedArticle.salesInformationId } : {}),
        position: position,
        ...(targetUrl ? { target_url: targetUrl } : {}),
      },
    ],
    event_data: {
      is_interaction_event: true,
      list_items_count: count,
      logged_in: loggedIn,
    },
  });

export const trackUpdateIconView = (areUpdatesAvailable: boolean) => {
  track({
    event_name: 'element_view',
    responsible_team_tracking: 'ENTS',
    responsible_team_feature: 'ENTS',
    event_data: {
      event_type: 'view',
      event_category: 'update_icon',
      event_content: areUpdatesAvailable ? 'item_updates_available' : 'no_item_updates_available',
      event_context: 'wishlist',
      is_interaction_event: false,
    },
  });
};

export const trackUpdateIconClick = (areUpdatesAvailable: boolean) => {
  track({
    event_name: 'element_click',
    responsible_team_tracking: 'ENTS',
    responsible_team_feature: 'ENTS',
    event_data: {
      event_type: 'click',
      event_category: 'update_icon',
      event_content: areUpdatesAvailable ? 'item_updates_available' : 'no_item_updates_available',
      event_context: 'wishlist',
      is_interaction_event: true,
    },
  });
};

export const trackChatBotInteractionEvent = (
  eventCategory: 'icon' | 'chatbot',
  eventName:
    | 'chatbot_item_click'
    | 'chatbot_cta_click'
    | 'chatbot_close'
    | 'chatbot_custom_message'
    | 'chatbot_predefined_message',
  eventType: 'click' | 'close' | 'send',
) => {
  track({
    event_name: eventName,
    responsible_team_tracking: 'ENTS',
    responsible_team_feature: 'ENTS',
    event_data: {
      event_type: eventType,
      event_category: eventCategory,
      event_context: 'algonomy_bot',
      is_interaction_event: true,
    },
  });
};

export const trackChatBotViewEvent = (
  eventCategory: 'icon' | 'chatbot',
  eventName: 'chatbot_cta_view' | 'chatbot_item_view',
) => {
  track({
    event_name: eventName,
    responsible_team_tracking: 'ENTS',
    responsible_team_feature: 'ENTS',
    event_data: {
      event_type: 'view',
      event_category: eventCategory,
      event_context: 'algonomy_bot',
      is_interaction_event: false,
    },
  });
};

export const trackChatBotOnboardingViewEvent = () => {
  track({
    event_name: 'chatbot_onboarding_view',
    responsible_team_tracking: 'ENTS',
    responsible_team_feature: 'ENTS',
    event_data: {
      event_type: 'view',
      event_category: 'chatbot',
      event_context: 'algonomy_bot',
      is_interaction_event: false,
    },
  });
};

export const trackChatBotOnboardingClick = () => {
  track({
    event_name: 'chatbot_onboarding_confirm',
    responsible_team_tracking: 'ENTS',
    responsible_team_feature: 'ENTS',
    event_data: {
      event_type: 'click',
      event_category: 'chatbot',
      event_context: 'algonomy_bot',
      is_interaction_event: true,
    },
  });
};
